import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Paper,
  Alert,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import TestNotificationForm from '../../../components/admin/notifications/TestNotificationForm';
import TemplateTestRunner from '../../../components/admin/notifications/TemplateTestRunner';
import DeliveryTester from '../../../components/admin/notifications/DeliveryTester';
import UserSimulator from '../../../components/admin/notifications/UserSimulator';

/**
 * NotificationTesting Page
 * Comprehensive testing tools for notification system
 *
 * Features:
 * - Send test notifications with custom content
 * - Batch test all notification templates
 * - Test delivery performance and reliability
 * - Simulate different user preference configurations
 */
const NotificationTesting: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/notifications')}
          sx={{ mb: 2 }}
        >
          Back to Notification Dashboard
        </Button>

        <Typography variant="h4" gutterBottom>
          Notification Testing Tools
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Test and validate notification functionality before deploying to production
        </Typography>
      </Box>

      {/* Important Notice */}
      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="body2">
          <strong>Testing Best Practices:</strong> Use test user accounts when possible. Test notifications
          are marked with special data flags to distinguish them from production notifications.
        </Typography>
      </Alert>

      {/* Testing Tools Grid */}
      <Grid container spacing={3}>
        {/* Test Notification Form */}
        <Grid item xs={12} lg={6}>
          <TestNotificationForm />
        </Grid>

        {/* Template Batch Tester */}
        <Grid item xs={12} lg={6}>
          <TemplateTestRunner />
        </Grid>

        {/* Delivery Performance Tester */}
        <Grid item xs={12} lg={6}>
          <DeliveryTester />
        </Grid>

        {/* User Preference Simulator */}
        <Grid item xs={12} lg={6}>
          <UserSimulator />
        </Grid>

        {/* Testing Tips */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Testing Tips & Best Practices
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  What to Test
                </Typography>
                <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Verify all notification templates render correctly
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Test different priority levels and their visual appearance
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Confirm user preferences are respected
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Validate delivery performance meets requirements
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Test action buttons and navigation links
                    </Typography>
                  </li>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Common Issues to Watch For
                </Typography>
                <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Long titles or messages causing layout issues
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Notifications not appearing when preferences are disabled
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Slow delivery times indicating performance issues
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Missing or broken action button links
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" color="text.secondary">
                      Template variables not being properly replaced
                    </Typography>
                  </li>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> Test notifications sent through these tools are marked with
                    special metadata (test: true) to help distinguish them from production notifications
                    in analytics and logs.
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default NotificationTesting;
